import { useMemo, useState } from 'react'
import { Download, Link2, Plus, RefreshCw, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  isLinkedPackagingDuplicateDocument,
  linkedPackagingCanonicalDocumentId,
} from '@/shared/lib/isLinkedPackagingDuplicateDocument'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Item, ItemActions, ItemContent, ItemTitle } from '@/components/ui/item'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { getErrorAction } from '@/features/product-catalog/utils/errorActions'
import {
  getMappedDocumentIngestionFailure,
  type IngestionFailureTranslate,
} from '@/features/product-catalog/utils/ingestionFailureMessage'
import type { PackagingDocumentLibraryPickerRow } from './PackagingDocumentLibraryPicker'
import { PackagingItemDocumentUploadDialog } from './PackagingItemDocumentUploadDialog'
import {
  DEFAULT_PACKAGING_ITEM_DOCUMENTS_PANEL_LABELS,
  type PackagingItemDocumentRequirements,
  type PackagingItemDocumentType,
  type PackagingItemDocumentsPanelLabels,
} from './packagingDocumentTypes'

export type PackagingItemDocumentRow = {
  id: string
  fileName: string
  fileSizeKb?: number | null
  documentType: PackagingItemDocumentType
  ingestionStatus?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | null
  ingestionError?: string | null
  ingestionErrorCode?: string | null
  ragDocumentId?: string | null
}

type PackagingItemDocumentsPanelProps = {
  documents: PackagingItemDocumentRow[]
  documentRequirements?: PackagingItemDocumentRequirements | null
  heavyMetalsStatus?: string | null
  substancesOfConcernStatus?: string | null
  isLoadingDocuments: boolean
  isUploading: boolean
  isDeleting: boolean
  isDownloading: boolean
  isDownloadingHeavyMetalsStatement?: boolean
  isDownloadingSubstancesOfConcernStatement?: boolean
  onUpload: (
    file: File,
    documentType: PackagingItemDocumentType
  ) => Promise<void>
  onAttach?: (
    documentId: string,
    documentType: PackagingItemDocumentType
  ) => Promise<void>
  onDelete: (documentId: string) => Promise<void>
  onDownload: (documentId: string) => Promise<void>
  libraryDocuments?: PackagingDocumentLibraryPickerRow[]
  isAttaching?: boolean
  onDownloadHeavyMetalsStatement?: () => Promise<void>
  onDownloadSubstancesOfConcernStatement?: () => Promise<void>
  labels?: PackagingItemDocumentsPanelLabels
  onUploadDialogOpenChange?: (open: boolean) => void
  /** When false, upload controls are hidden (downloads remain available). */
  canUpload?: boolean
  /** When false, delete buttons are hidden. */
  canDelete?: boolean
  /** Artem admin: batch RAG ingest controls. Status is visible to all users. */
  showIngestionAdmin?: boolean
  isIngestingAll?: boolean
  onIngestAll?: () => Promise<void>
  ingestingDocumentId?: string | null
  onIngestDocument?: (documentId: string) => Promise<void>
  /** Org writers: retry failed ingestion for outage-class error codes only. */
  onRetryDocument?: (documentId: string) => Promise<void>
  retryingDocumentId?: string | null
}

function formatFileSizeKb(fileSizeKb?: number | null) {
  if (fileSizeKb == null) {
    return null
  }
  if (fileSizeKb >= 1024) {
    return `${(fileSizeKb / 1024).toFixed(1)} MB`
  }
  return `${fileSizeKb} KB`
}

function ingestionStatusVariant(
  status: PackagingItemDocumentRow['ingestionStatus']
): 'default' | 'secondary' | 'success' | 'destructive' | 'outline' {
  if (status === 'COMPLETED') {
    return 'success'
  }
  if (status === 'FAILED') {
    return 'destructive'
  }
  if (status === 'PROCESSING' || status === 'PENDING') {
    return 'secondary'
  }
  return 'outline'
}

function formatIngestionStatusLabel(
  document: PackagingItemDocumentRow,
  labels: PackagingItemDocumentsPanelLabels
) {
  if (!document.ingestionStatus) {
    return labels.ingestionNotStarted ?? 'Not ingested'
  }
  return (
    labels.ingestionStatusLabels?.[document.ingestionStatus] ??
    document.ingestionStatus
  )
}

function documentIngestActionLabel(
  document: PackagingItemDocumentRow,
  labels: PackagingItemDocumentsPanelLabels
) {
  if (document.ingestionStatus === 'COMPLETED') {
    return labels.reIngestDocument ?? 'Re-ingest'
  }
  return labels.ingestDocument ?? 'Ingest'
}

export function PackagingItemDocumentsPanel({
  documents,
  documentRequirements,
  heavyMetalsStatus,
  substancesOfConcernStatus,
  isLoadingDocuments,
  isUploading,
  isDeleting,
  isDownloading,
  isDownloadingHeavyMetalsStatement = false,
  isDownloadingSubstancesOfConcernStatement = false,
  onUpload,
  onAttach,
  onDelete,
  onDownload,
  libraryDocuments = [],
  isAttaching = false,
  onDownloadHeavyMetalsStatement,
  onDownloadSubstancesOfConcernStatement,
  labels = DEFAULT_PACKAGING_ITEM_DOCUMENTS_PANEL_LABELS,
  onUploadDialogOpenChange,
  canUpload = true,
  canDelete = true,
  showIngestionAdmin = false,
  isIngestingAll = false,
  onIngestAll,
  ingestingDocumentId = null,
  onIngestDocument,
  onRetryDocument,
  retryingDocumentId = null,
}: PackagingItemDocumentsPanelProps) {
  const { t: tProductCatalog } = useTranslation('productCatalog')
  const [dialogOpen, setDialogOpen] = useState(false)

  const translateIngestionFailure: IngestionFailureTranslate = (
    key,
    defaultValue
  ) => tProductCatalog(`sources.${key}`, { defaultValue })

  const missingRequiredTypes = documentRequirements?.missingRequiredTypes ?? []
  const requiredTypes = documentRequirements?.requiredTypes ?? []

  const existingDocumentTypes = useMemo(
    () => documents.map((document) => document.documentType),
    [documents]
  )

  const sortedDocuments = [...documents].sort((left, right) =>
    left.fileName.localeCompare(right.fileName)
  )

  return (
    <section className='space-y-3'>
      <div className='flex flex-wrap items-center justify-between gap-2'>
        <h3 className='text-lg font-semibold'>{labels.title}</h3>
        <div className='flex flex-wrap items-center gap-2'>
          {showIngestionAdmin && onIngestAll && (
            <Button
              type='button'
              variant='secondary'
              size='sm'
              disabled={isIngestingAll || sortedDocuments.length === 0}
              onClick={() => {
                void onIngestAll()
              }}
            >
              {isIngestingAll
                ? (labels.ingestingDocuments ?? 'Ingesting…')
                : (labels.ingestDocuments ?? 'Ingest documents')}
            </Button>
          )}
          {canUpload && (
            <Button
              type='button'
              variant='outline'
              size='sm'
              className='gap-1'
              disabled={isUploading}
              onClick={() => {
                setDialogOpen(true)
              }}
            >
              <Plus className='size-4' />
              {isUploading ? labels.uploading : labels.uploadDocument}
            </Button>
          )}
        </div>
      </div>

      {requiredTypes.length > 0 && (
        <div className='space-y-2'>
          <div className='flex flex-wrap items-center justify-between gap-2'>
            <h3 className='text-sm font-medium'>{labels.mandatoryDocuments}</h3>
            {missingRequiredTypes.length > 0 && (
              <p className='text-sm text-destructive'>
                {labels.missingMandatoryDocuments}
              </p>
            )}
          </div>
          <div className='grid gap-2 sm:grid-cols-2 lg:grid-cols-3'>
            {requiredTypes.map((documentType) => {
              const isComplete = !missingRequiredTypes.includes(documentType)
              const showHeavyMetalsStatementDownload =
                documentType === 'HEAVY_METALS_CONFORMITY_STATEMENT' &&
                heavyMetalsStatus === 'NONE' &&
                onDownloadHeavyMetalsStatement != null
              const showSubstancesOfConcernStatementDownload =
                documentType === 'SUBSTANCES_OF_CONCERN_CONFORMITY_STATEMENT' &&
                substancesOfConcernStatus === 'NONE' &&
                onDownloadSubstancesOfConcernStatement != null
              return (
                <Item key={documentType} variant='outline'>
                  <ItemContent>
                    <ItemTitle>
                      {labels.shortTypeLabels[documentType]}
                    </ItemTitle>
                  </ItemContent>
                  <ItemActions className='flex flex-col items-end gap-2 sm:flex-row sm:items-center'>
                    {showHeavyMetalsStatementDownload && (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='gap-1'
                        disabled={isDownloadingHeavyMetalsStatement}
                        onClick={() => {
                          void onDownloadHeavyMetalsStatement()
                        }}
                      >
                        <Download className='size-4' />
                        {labels.downloadHeavyMetalsStatement ??
                          'Download HM statement'}
                      </Button>
                    )}
                    {showSubstancesOfConcernStatementDownload && (
                      <Button
                        type='button'
                        variant='outline'
                        size='sm'
                        className='gap-1'
                        disabled={isDownloadingSubstancesOfConcernStatement}
                        onClick={() => {
                          void onDownloadSubstancesOfConcernStatement()
                        }}
                      >
                        <Download className='size-4' />
                        {labels.downloadSubstancesOfConcernStatement ??
                          'Download SoC statement'}
                      </Button>
                    )}
                    <Badge variant={isComplete ? 'success' : 'destructive'}>
                      {isComplete
                        ? labels.mandatoryComplete
                        : labels.mandatoryMissing}
                    </Badge>
                  </ItemActions>
                </Item>
              )
            })}
          </div>
        </div>
      )}

      {isLoadingDocuments && (
        <p className='text-sm text-muted-foreground'>
          {labels.loadingDocuments}
        </p>
      )}

      {!isLoadingDocuments && sortedDocuments.length === 0 && (
        <p className='text-sm text-muted-foreground'>{labels.noDocuments}</p>
      )}

      {!isLoadingDocuments && sortedDocuments.length > 0 && (
        <div className='overflow-x-auto rounded-md border'>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className='h-9'>{labels.columnType}</TableHead>
                <TableHead className='h-9'>{labels.columnFile}</TableHead>
                <TableHead className='h-9 w-[5.5rem]'>
                  {labels.columnSize}
                </TableHead>
                <TableHead className='h-9 w-[8rem]'>
                  {labels.columnIngestion ?? 'Ingestion'}
                </TableHead>
                <TableHead className='h-9 w-[11rem] text-end'>
                  <span className='sr-only'>{labels.download}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDocuments.map((document) => {
                const sizeLabel = formatFileSizeKb(document.fileSizeKb)
                const ingestionFailureDetail = getMappedDocumentIngestionFailure(
                  document.ingestionStatus,
                  document.ingestionErrorCode,
                  translateIngestionFailure
                )
                const isLinkedDuplicate =
                  showIngestionAdmin &&
                  isLinkedPackagingDuplicateDocument(document)
                const canonicalDocumentId = isLinkedDuplicate
                  ? linkedPackagingCanonicalDocumentId(document)
                  : null
                const linkedDuplicateTooltip =
                  canonicalDocumentId != null
                    ? (labels.linkedDuplicateTooltip ?? '').replace(
                        '{{canonicalDocumentId}}',
                        canonicalDocumentId
                      )
                    : null
                const ingestionBadge = (
                  <Badge
                    variant={ingestionStatusVariant(document.ingestionStatus)}
                  >
                    {formatIngestionStatusLabel(document, labels)}
                  </Badge>
                )
                const ingestActionLabel = documentIngestActionLabel(
                  document,
                  labels
                )
                const isIngestingThisDocument =
                  ingestingDocumentId === document.id
                const canIngestThisDocument =
                  showIngestionAdmin &&
                  onIngestDocument != null &&
                  !isLinkedDuplicate &&
                  document.ingestionStatus !== 'PENDING' &&
                  document.ingestionStatus !== 'PROCESSING'
                const retryErrorAction =
                  document.ingestionStatus === 'FAILED'
                    ? getErrorAction(document.ingestionErrorCode)
                    : null
                const canRetryThisDocument =
                  onRetryDocument != null &&
                  !showIngestionAdmin &&
                  !isLinkedDuplicate &&
                  retryErrorAction?.action === 'retry'
                const isRetryingThisDocument =
                  retryingDocumentId === document.id
                const RetryActionIcon = retryErrorAction?.icon
                return (
                  <TableRow key={`${document.id}:${document.documentType}`}>
                    <TableCell className='py-2 text-sm font-medium whitespace-nowrap'>
                      {labels.shortTypeLabels[document.documentType]}
                    </TableCell>
                    <TableCell className='max-w-[18rem] py-2 text-sm'>
                      <div className='flex min-w-0 items-center gap-2'>
                        <span className='truncate' title={document.fileName}>
                          {document.fileName}
                        </span>
                        {isLinkedDuplicate && (
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant='outline'
                                className='shrink-0 gap-1 font-normal'
                              >
                                <Link2 className='size-3' aria-hidden />
                                {labels.linkedDuplicateBadge ??
                                  'Linked duplicate'}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent className='max-w-sm text-left'>
                              {linkedDuplicateTooltip}
                            </TooltipContent>
                          </Tooltip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className='py-2 text-sm text-muted-foreground tabular-nums'>
                      {sizeLabel ?? '—'}
                    </TableCell>
                    <TableCell className='py-2'>
                      {ingestionFailureDetail ? (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span
                              tabIndex={0}
                              className='inline-flex cursor-default rounded-sm outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2'
                              aria-label={ingestionFailureDetail}
                            >
                              {ingestionBadge}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent className='max-w-xs text-left'>
                            {ingestionFailureDetail}
                          </TooltipContent>
                        </Tooltip>
                      ) : (
                        ingestionBadge
                      )}
                    </TableCell>
                    <TableCell className='py-2 text-end'>
                      <div className='inline-flex items-center gap-1'>
                        {canIngestThisDocument && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            className='h-8 gap-1 px-2'
                            disabled={isIngestingAll || isIngestingThisDocument}
                            aria-label={ingestActionLabel}
                            onClick={() => {
                              void onIngestDocument(document.id)
                            }}
                          >
                            <RefreshCw
                              className={
                                isIngestingThisDocument
                                  ? 'size-3.5 animate-spin'
                                  : 'size-3.5'
                              }
                            />
                            {ingestActionLabel}
                          </Button>
                        )}
                        {canRetryThisDocument &&
                          retryErrorAction != null &&
                          RetryActionIcon != null && (
                            <Button
                              type='button'
                              variant='ghost'
                              size='sm'
                              className='h-8 gap-1 px-2'
                              disabled={isRetryingThisDocument}
                              aria-label={tProductCatalog(
                                retryErrorAction.labelKey
                              )}
                              onClick={() => {
                                void onRetryDocument(document.id)
                              }}
                            >
                              <RetryActionIcon
                                className={
                                  isRetryingThisDocument
                                    ? 'size-3.5 animate-spin'
                                    : 'size-3.5'
                                }
                                aria-hidden
                              />
                              {tProductCatalog(retryErrorAction.labelKey)}
                            </Button>
                          )}
                        <Button
                          type='button'
                          variant='ghost'
                          size='icon'
                          className='size-8'
                          disabled={isDownloading}
                          aria-label={labels.download}
                          onClick={() => {
                            void onDownload(document.id)
                          }}
                        >
                          <Download className='size-3.5' />
                        </Button>
                        {canDelete && (
                          <Button
                            type='button'
                            variant='ghost'
                            size='icon'
                            className='size-8'
                            disabled={isDeleting}
                            aria-label={labels.delete}
                            onClick={() => {
                              void onDelete(document.id)
                            }}
                          >
                            <Trash2 className='size-3.5' />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </div>
      )}

      {canUpload && (
        <PackagingItemDocumentUploadDialog
          open={dialogOpen}
          onOpenChange={(open) => {
            setDialogOpen(open)
            onUploadDialogOpenChange?.(open)
          }}
          isUploading={isUploading}
          isAttaching={isAttaching}
          existingDocumentTypes={existingDocumentTypes}
          libraryDocuments={libraryDocuments}
          itemDocuments={documents.map((document) => ({
            id: document.id,
            documentType: document.documentType,
          }))}
          labels={{
            title: labels.uploadDialogTitle,
            descriptionIntro: labels.uploadDialogDescription,
            requirementFormats: labels.uploadDialogRequirementFormats,
            requirementMaxSize: labels.uploadDialogRequirementMaxSize,
            documentType: labels.documentType,
            selectDocumentType: labels.selectDocumentType,
            dragDrop: labels.uploadDialogDragDrop,
            orClick: labels.uploadDialogOrClick,
            selectFiles: labels.uploadDialogSelectFiles,
            uploading: labels.uploading,
            uploadFailed: labels.uploadFailed,
            uploadRightsProtected: labels.uploadRightsProtected,
            uploadEncrypted: labels.uploadEncrypted,
            uploadInvalidPdf: labels.uploadInvalidPdf,
            uploadInvalidLegacyXls: labels.uploadInvalidLegacyXls,
            uploadFileTooLarge: labels.uploadFileTooLarge,
            uploadUnsupportedFileType: labels.uploadUnsupportedFileType,
            uploadEmptyFile: labels.uploadEmptyFile,
            uploadDialogAssessingFile: labels.uploadDialogAssessingFile,
            done: labels.uploadDialogDone,
            multipleDropped: labels.uploadDialogMultipleDropped,
            confirmUpload: labels.uploadDialogConfirmUpload,
            chooseDifferentFile: labels.uploadDialogChooseDifferentFile,
            librarySectionTitle: labels.librarySectionTitle,
            librarySearchPlaceholder: labels.librarySearchPlaceholder,
            libraryEmpty: labels.libraryEmpty,
            libraryEmptyAllLinked: labels.libraryEmptyAllLinked,
            librarySearchEmpty: labels.librarySearchEmpty,
            libraryLinkedItemCount: labels.libraryLinkedItemCount,
            libraryAttaching: labels.libraryAttaching,
            sameNameWarningTitle: labels.sameNameWarningTitle,
            sameNameWarningDescription: labels.sameNameWarningDescription,
            sameNameAlreadyLinkedDescription:
              labels.sameNameAlreadyLinkedDescription,
            sameNameReuseCta: labels.sameNameReuseCta,
            attachSuccess: labels.attachSuccess,
            uploadSuccess: labels.uploadSuccess,
            attachFailed: labels.attachFailed,
            typeAlreadyUploaded: labels.uploadDialogTypeAlreadyUploaded,
            typeLabels: labels.shortTypeLabels,
          }}
          onUpload={onUpload}
          onAttach={onAttach}
        />
      )}
    </section>
  )
}
